import React from 'react';
import Markdown from 'markdown-to-jsx';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';
import { Box, Stack } from '@atlaskit/primitives';
import {
	srcDark as srcGenericErrorDark,
	srcLight as srcGenericErrorLight,
} from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/error/components/generic-error/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { Anchor } from '../../../common/ui/anchor/index.tsx';
import { TransitionHomeContentBox } from '../../../common/ui/transition-home/content-box/index.tsx';
import { TransitionHomeHeader } from '../../../common/ui/transition-home/header/index.tsx';
import { messages as stepMessages } from '../../../common/ui/transition-home/step/messages.tsx';
import taskMessages from '../../../common/utils/tasks/messages.tsx';
import { useGlobalChangeboarding } from '../../../controllers/store/global/index.tsx';
import { useDeprecatedHeartbeats } from '../../../services/store/deprecated-counts/index.tsx';
import { messages } from './messages.tsx';

export const HeartbeatsStep = () => {
	const { formatMessage } = useIntl();

	const { opsgenieBaseUrl } = useGlobalChangeboarding();

	const [
		{
			hasDeprecatedGlobalHeartbeats,
			hasDeprecatedTotalHeartbeats,
			isExceedHeartbeatLimit,
			heartbeats,
		},
	] = useDeprecatedHeartbeats();

	if (!heartbeats.data) {
		throw new Error('Heartbeats step - heartbeat counts data should be loaded at this point');
	}

	const { globalHeartbeatCount, totalHeartbeatCount } = heartbeats.data;

	const ogHeartbeatsUrl = opsgenieBaseUrl ? `${opsgenieBaseUrl}/settings/heartbeat` : '';

	return (
		<Stack space="space.250">
			<TransitionHomeHeader
				title={formatMessage(stepMessages.heartbeatsTitleNonFinal)}
				description={formatMessage(stepMessages.heartbeatsDescriptionNonFinal)}
			/>
			{/* Task One */}
			{hasDeprecatedTotalHeartbeats && (
				<TransitionHomeContentBox>
					<Stack space="space.200">
						<Heading as="h2" size="small">
							{formatMessage(taskMessages.heartbeatsTaskOneNonFinal)}
						</Heading>
						<Box>
							<Markdown options={{ overrides: { a: { component: Anchor } } }}>
								{formatMessage(messages.taskOneDescriptionNonFinal, {
									totalHeartbeatCount,
								})}
							</Markdown>
						</Box>
					</Stack>
				</TransitionHomeContentBox>
			)}

			{/* Task Two */}
			{hasDeprecatedGlobalHeartbeats && (
				<TransitionHomeContentBox
					illustration={
						<Image width="163px" src={srcGenericErrorLight} srcDark={srcGenericErrorDark} alt="" />
					}
				>
					<Stack space="space.200">
						<Heading as="h2" size="small">
							{formatMessage(taskMessages.heartbeatsTaskTwoNonFinal)}
						</Heading>
						<Stack space="space.150">
							<Markdown options={{ overrides: { a: { component: Anchor } } }}>
								{formatMessage(messages.taskTwoDescriptionNonFinal, {
									globalHeartbeatCount,
								})}
							</Markdown>
						</Stack>
					</Stack>
				</TransitionHomeContentBox>
			)}
			{/* Task Three */}
			{isExceedHeartbeatLimit && (
				<TransitionHomeContentBox>
					<Stack space="space.200">
						<Heading as="h2" size="small">
							{formatMessage(taskMessages.heartbeatsTaskThreeNonFinal)}
						</Heading>
						<Stack space="space.150">
							<Markdown options={{ overrides: { a: { component: Anchor } } }}>
								{formatMessage(messages.taskThreeDescriptionNonFinal, {
									heartbeatToBeRemovedCount: totalHeartbeatCount - 2000,
								})}
							</Markdown>
						</Stack>
						<Box>
							<Button href={ogHeartbeatsUrl} target="_blank">
								{formatMessage(messages.taskThreeButtonNonFinal)}
							</Button>
						</Box>
					</Stack>
				</TransitionHomeContentBox>
			)}
		</Stack>
	);
};
